import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter, ElementRef, HostListener, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FilterGroup } from '../../models';
import { FilterGroupComponent } from '../filter-group/filter-group.component';
import { FormsModule } from '@angular/forms';

/**
 * FilterPanel — Presentation Component
 *
 * The full sidebar filter panel containing multiple FilterGroupComponents
 * and the publication year range filter.
 *
 * Emits events upward — does NOT modify state directly.
 */
@Component({
  selector: 'app-filter-panel',
  standalone: true,
  imports: [CommonModule, TranslateModule, FilterGroupComponent, FormsModule],
  templateUrl: './filter-panel.component.html',
  styleUrl: './filter-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterPanelComponent {
  private elementRef = inject(ElementRef);
  private translate = inject(TranslateService);

  get isRtl(): boolean {
    return this.translate.currentLang === 'ar';
  }

  @Input({ required: true }) isFilterPanelOpen: boolean = false;
  /** All filter groups with their options and selections */
  @Input({ required: true }) filterGroups: FilterGroup[] = [];

  /** Emitted when a checkbox is toggled */
  @Output() filterToggle = new EventEmitter<{ groupKey: string; value: string }>();

  /** Emitted when the user types in a facet's search box */
  @Output() searchFacet = new EventEmitter<{ groupKey: string; term: string }>();

  /** Emitted when the user clicks 'Show More' on a facet */
  @Output() loadMoreFacet = new EventEmitter<string>();

  /** Emitted when a filter group is cleared */
  @Output() filterGroupClear = new EventEmitter<string>();

  /** Emitted when all filters are cleared */
  @Output() clearAll = new EventEmitter<void>();

  /** Emitted when "تطبيق الاختيارات" (Apply Selections) is clicked */
  @Output() applyFilters = new EventEmitter<void>();

  /** Emitted when panel should close */
  @Output() closePanel = new EventEmitter<void>();
  @Output() closeFilterPanel = new EventEmitter<void>();

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isFilterPanelOpen) return;
    const target = event.target as HTMLElement;

    // Check if click was inside this component or on a toggle button
    const isInside = this.elementRef.nativeElement.contains(target);
    const isToggleButton = target.closest('.btn-advanced') ||
                           target.closest('.filter-toggle-btn') ||
                           target.closest('.btn-filter-toggle') ||
                           target.closest('.search-bar-container');

    if (!isInside && !isToggleButton) {
      this.onClose();
    }
  }

  onFilterToggle(groupKey: string, value: any): void {
    this.filterToggle.emit({ groupKey, value });
  }

  onSearchFacet(groupKey: string, term: string): void {
    this.searchFacet.emit({ groupKey, term });
  }

  onLoadMoreFacet(groupKey: string): void {
    this.loadMoreFacet.emit(groupKey);
  }

  onClearAll(): void {
    this.clearAll.emit();
  }

  onClose(): void {
    this.closePanel.emit();
    this.closeFilterPanel.emit();
  }
}
